import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";

/**
 * Two-step invite flow to prevent one-time tokens from being consumed by
 * crawlers / link-preview services (WhatsApp, Telegram, iMessage, etc.).
 *
 * Step 1 (page load): GET /invite/:token/preview — read-only status check.
 * Step 2 (user click): POST /invite/:token/claim — consumes token & redirects.
 *
 * Server also filters crawler user-agents on POST as a second safety net.
 */
export default function InvitePage() {
  const { token } = useParams();
  const [state, setState] = useState("loading");
  const [claiming, setClaiming] = useState(false);
  const { t } = useI18n();

  useEffect(() => {
    api
      .get(`/invite/${token}/preview`)
      .then((r) => {
        const s = r.data?.status;
        if (s === "valid") setState("ready");
        else setState(s === "used" || s === "expired" ? "expired" : "expired");
      })
      .catch(() => setState("expired"));
  }, [token]);

  const handleClaim = async () => {
    if (claiming) return;
    setClaiming(true);
    try {
      const r = await api.post(`/invite/${token}/claim`);
      if (r.data?.status === "valid" && r.data?.invite_link) {
        window.location.href = r.data.invite_link;
      } else {
        setState("expired");
        setClaiming(false);
      }
    } catch {
      setState("expired");
      setClaiming(false);
    }
  };

  return (
    <main className="min-h-screen flex items-center justify-center bg-[#FDFBF7] px-6 pt-24 pb-16" data-testid="invite-page">
      <div className="max-w-md w-full bg-white border border-stone-200 p-8 md:p-12 text-center">
        {state === "loading" && (
          <div data-testid="invite-loading">
            <Loader2 size={44} className="mx-auto text-[#F39237] animate-spin mb-6" />
            <p className="text-stone-600">{t("invite.checking", "Memeriksa link undangan Anda…")}</p>
          </div>
        )}

        {state === "ready" && (
          <div data-testid="invite-ready">
            <CheckCircle2 size={48} className="mx-auto text-green-600 mb-5" />
            <h1 className="font-display text-2xl md:text-3xl mb-3">{t("invite.valid_title", "Link valid! ✅")}</h1>
            <p className="text-stone-600 text-sm mb-3">
              {t(
                "invite.valid_desc",
                "Selamat! Klik tombol di bawah untuk bergabung ke grup Telegram khusus Mitra Aurora."
              )}
            </p>
            <p className="text-xs text-stone-400 mb-8" data-testid="invite-warning">
              {t("invite.one_time_warning", "⚠️ Link ini hanya berlaku SEKALI KLIK dan langsung hangus setelahnya.")}
            </p>
            <button
              onClick={handleClaim}
              disabled={claiming}
              data-testid="invite-open-telegram"
              className="inline-flex items-center justify-center w-full px-7 py-4 bg-[#0A0A0A] text-white hover:bg-[#F39237] hover:text-[#0A0A0A] transition-colors disabled:opacity-50"
            >
              {claiming ? (
                <>
                  <Loader2 size={18} className="animate-spin mr-2" />
                  {t("invite.opening", "Membuka grup Telegram…")}
                </>
              ) : (
                t("invite.open_telegram", "Buka Grup Telegram")
              )}
            </button>
          </div>
        )}

        {state === "expired" && (
          <div data-testid="invite-expired">
            <XCircle size={48} className="mx-auto text-red-500 mb-5" />
            <h1 className="font-display text-2xl md:text-3xl mb-3">{t("invite.expired_title", "Link Tidak Berlaku")}</h1>
            <p className="text-stone-600 text-sm mb-8 break-words" data-testid="invite-expired-message">
              {t(
                "invite.expired_desc",
                "Link sudah kedaluwarsa atau sudah digunakan. Silakan hubungi admin pusat di Telegram @CS_aurorahijab untuk verifikasi ulang."
              )}
            </p>
            <div className="flex flex-col gap-3">
              <a
                href="https://t.me/CS_aurorahijab"
                target="_blank"
                rel="noreferrer"
                data-testid="invite-contact-admin"
                className="inline-flex items-center justify-center px-7 py-4 bg-[#0A0A0A] text-white hover:bg-[#F39237] hover:text-[#0A0A0A] transition-colors"
              >
                {t("invite.contact_admin", "Hubungi Admin @CS_aurorahijab")}
              </a>
              <a href="/" data-testid="invite-back-home" className="text-sm text-stone-500 hover:text-[#F39237]">
                {t("invite.back_home", "Kembali ke Beranda")}
              </a>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
